import {
  Row,
  Table,
  TableBody,
  TableHeader,
  TableLayout,
} from "@/app/components/table"
import { memo, useCallback, useState } from "react"
import { type TableComponents, TableVirtuoso } from "react-virtuoso"

export enum ItemType {
  CONTENT = "content",
  PLACEHOLDER = "placeholder",
}

export type TablePlaceholderItem = { type: ItemType.PLACEHOLDER }

export type TableItem<T> =
  | { type: ItemType.CONTENT; content: T }
  | TablePlaceholderItem

export type TableRowType<T> = Required<
  TableComponents<TableItem<T>>
>["TableRow"]

interface Props<T> {
  items: TableItem<T>[]
  loadNext(): void
  components: {
    HeaderRow: React.ComponentType
    TableRow?: TableRowType<T>
    RowContent: React.ComponentType<{ content: T; index: number }>
    RowSkeleton: React.ComponentType
    EmptyMessage?: React.ComponentType
  }
  getItemKey: (item: T) => string
  initialItemCount?: number
  /**
   * @default true
   */
  useWindowScroll?: boolean
  dataTestId?: string
}

const genericMemo: <T>(component: T) => T = memo

const TableItemContent = genericMemo(function TableItemContent<T>({
  item,
  index,
  RowContent,
  RowSkeleton,
}: {
  item: TableItem<T>
  index: number
  RowContent: React.ComponentType<{ content: T; index: number }>
  RowSkeleton: React.ComponentType
}) {
  if (item.type === ItemType.PLACEHOLDER) {
    return <RowSkeleton />
  }

  return <RowContent content={item.content} index={index} />
})

const COMPONENTS = {
  Table,
  TableHead: TableHeader,
  TableBody,
}

export function InfiniteTable<T>({
  items,
  loadNext,
  components,
  getItemKey,
  initialItemCount,
  useWindowScroll = true,
  dataTestId,
}: Props<T>) {
  const { HeaderRow, TableRow = Row } = components

  if (!items.length) {
    return <EmptyTable EmptyMessage={components.EmptyMessage} />
  }

  const fixedHeaderContent = useCallback(() => <HeaderRow />, [HeaderRow])

  const itemContent = useCallback(
    (index: number, item: TableItem<T>) => (
      <TableItemContent item={item} {...components} index={index} />
    ),
    [components],
  )

  const computeItemKey = useCallback(
    (index: number, item: TableItem<T>) => {
      if (item.type === ItemType.PLACEHOLDER) {
        return `placeholder-${index}`
      }
      return getItemKey(item.content)
    },
    [getItemKey],
  )

  return (
    <TableVirtuoso
      className="box-content overflow-x-auto overflow-y-hidden pb-2"
      components={{ ...COMPONENTS, TableRow }}
      data={items}
      fixedHeaderContent={fixedHeaderContent}
      endReached={loadNext}
      increaseViewportBy={500}
      itemContent={itemContent}
      computeItemKey={computeItemKey}
      useWindowScroll={useWindowScroll}
      initialItemCount={
        initialItemCount && Math.min(initialItemCount, items.length)
      }
      data-testid={dataTestId}
    />
  )
}

function EmptyTable({ EmptyMessage }: { EmptyMessage?: React.ComponentType }) {
  return (
    <div className="flex h-full flex-1 items-center justify-center">
      {EmptyMessage ? (
        <EmptyMessage />
      ) : (
        <h2 className="text-center text-2xl">Nothing here yet...</h2>
      )}
    </div>
  )
}

const PLACEHOLDERS = Array.from({ length: 20 }).map((_, idx) => idx)

export function TableSkeleton({
  HeaderRow,
  RowSkeleton,
}: {
  HeaderRow: React.ComponentType
  RowSkeleton: React.ComponentType
}) {
  return (
    <div className="h-full w-full overflow-auto">
      <TableLayout
        header={<HeaderRow />}
        body={PLACEHOLDERS.map((idx) => (
          <Row key={idx}>
            <RowSkeleton />
          </Row>
        ))}
      />
    </div>
  )
}
